import { CountryCode } from 'libphonenumber-js';
import { LeadWebhookFormat } from './BusinessConfig';
import { AttrWindowsBySourceType } from '../insights';
import { User } from '../user';
import { ExtrapolationType, StageTarget, Support } from '../funnel';

export type LegacyBusinessUpdateable = Partial<
  Pick<
    LegacyBusiness,
    'first_initial_purchase' | 'yvc_party_size' | 'yvc_spend' | 'yvc_visits'
  >
>;

/**
 * Shared externally (with other apps not in our code base).
 *
 * Should not change.
 */
export interface BusinessPublic {
  id: number;
  businessName: string;
}

export const exampleBusinessPublic: BusinessPublic = {
  id: 1,
  businessName: 'Example Business',
};

export enum BusinessPhase {
  CRAWL = 1,
  WALK = 2,
  RUN = 3,
  FLY = 4,
}

export interface Business extends BusinessPublic {
  branchID: number;
  branchName: string;
  podID: number;
  podName: string;
  isLegacy: boolean;
  currencyType: string;
  locale: string;
  timezone: string;
  fbPageImgURL?: string;
  fbAccountID: string;
  fbAdAuthorizerID?: number | null;
  fbPageID?: string;
  googleType?: string;
  latitude?: number;
  longitude?: number;
  fbOfflineEventSetID?: string;
  fbDatasetID?: string;
  startDate: string | null | undefined;
  errors?: BusinessError[];
  semRushID?: number | null;
  googleLookerEmbedURL?: string | null;
  reportsPageActive?: boolean;
  dataUploaderActive?: boolean;
  pixelIndicator?: string;
  dataIndicator?: string;
  uploadIndicator?: string;
  pixelIndicatorDesc?: string;
  dataIndicatorDesc?: string;
  uploadIndicatorDesc?: string;
  allowLeadCollection?: boolean;
  country?: CountryCode;
  googleAdWordsClientCustomerID?: string | null;
  closePercentage?: number | null;
  showROI?: boolean;
  monthlySpend?: number | null;
  mktgRetainer?: number | null;
  yearlyPartySize?: number | null;
  yearlyVisits?: number | null;
  yearlyValueSpend?: number | null;
  website?: string;
  googleDriveFolderID?: string | null;
  googleAdsCustomerID?: string | null;
  phase?: BusinessPhase;
  slackChannelId?: string | null;
  // The business's Salesforce Account id. Empty means nobody has connected this
  // business to Salesforce yet — the Sprint Packet survey card reads it to name
  // that as the reason it has no survey link.
  sfAccountId?: string | null;
}

export const exampleBusiness: Business = {
  branchID: 1,
  branchName: 'Test',
  podID: 1,
  podName: 'Test',
  isLegacy: false,
  currencyType: 'USD',
  locale: 'en-US',
  timezone: 'America/Chicago',
  fbPageImgURL: 'fb.com/imglink',
  fbAccountID: '123',
  fbPageID: '321',
  fbOfflineEventSetID: '1234',
  startDate: new Date().toString(),
  semRushID: 123,
  googleLookerEmbedURL: 'google.com/embed',
  reportsPageActive: true,
  dataUploaderActive: true,
  pixelIndicator: 'Facebook Pixel Activity',
  dataIndicator: 'Data Activity',
  uploadIndicator: 'Upload Activity',
  pixelIndicatorDesc:
    'This shows that your Facebook Pixel is active on your website',
  dataIndicatorDesc:
    'This indicator shows that your data source is linked into our system',
  uploadIndicatorDesc:
    'This indicator shows that your data has been processed by Marketing Milk',
  allowLeadCollection: true,
  country: 'US',
  googleAdWordsClientCustomerID: '123',
  closePercentage: 1,
  showROI: true,
  monthlySpend: 1000,
  mktgRetainer: 1000,
  yearlyPartySize: 1000,
  yearlyVisits: 1000,
  yearlyValueSpend: 1000,
  website: 'example.com',
  phase: BusinessPhase.CRAWL,
  ...exampleBusinessPublic,
};

export enum BusinessError {
  ad_account_access_error = 'ad_account_access_error',
  page_access_error = 'page_access_error',
  offline_upload_error = 'offline_upload_error',
  google_ads_access_error = 'google_ads_access_error',
}

export type AuthorizerConnectionType = 'fb_ad' | 'fb_page';

export interface AuthorizerConnection {
  business_id: number;
  business_name: string;
  connection_type: AuthorizerConnectionType;
  errors?: BusinessError[];
}

export interface AuthorizerConnectionsByUser {
  user_id: number;
  user_name: string;
  user_email: string;
  user_profile_pic?: string | null;
  connections: AuthorizerConnection[];
}

export interface AuthorizationsPage {
  data: AuthorizerConnectionsByUser[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuthorizerTokenStatus {
  user_id: number;
  is_valid: boolean;
  expires_at?: number | null;
  scopes?: string[];
}

export interface BusinessManagerBusiness {
  errors?: BusinessError[];
  id?: number;
  main: BusinessManagerBusinessMain;
  funnel: {
    active: number | null;
    preset: number;
    stages: BusinessManagerStage[];
  };
  users: BusinessManagerBusinessUser[];
  options: BusinessManagerOption[];
  tags: number[];
  dataSources: number[];
}

export interface BusinessManagerOption {
  branch_id: number;
  business_id: number;
  branch_options_id: number;
  field_value: string;
}

export interface BusinessManagerBusinessUser {
  user_id: number;
  receive_leads: boolean;
  receive_status: boolean;
}

export interface BusinessManagerBusinessMain {
  business_name: string;
  website: string;
  fb_account_id: string;
  fb_account_name: string | null;
  fb_page_id: string | null;
  fb_page_name: string | null;
  mktg_retainer: number;
  yearly_value: number;
  start: string;
  branch_id: number | null;
  pod_id: number | null;
  fb_dataset_id: string | null;
  fb_dataset_name: string | null;
  instagram_id: string | null;
  instagram_name: string | null;
  offline_event_set_id: string | null;
  fb_offline_name: string | null;
  monthly_spend: number;
  ga_view_id: number | null;
  client_customer_id: number | null;
  goal_text: string;
  yvc_party_size: number;
  yvc_visits: number;
  yvc_spend: number;
  ingest_access_token: string;
  close_percentage: number;
  timezone: string;
  attr_windows: AttrWindowsBySourceType;
  show_roi: boolean;
  pixel_indicator: string;
  pixel_indicator_desc: string;
  data_indicator: string;
  data_indicator_desc: string;
  upload_indicator: string;
  upload_indicator_desc: string;
  first_initial_purchase: number;
  phase: number;
  city: string;
  state: string;
  lead_webhook_format: LeadWebhookFormat;
  currency_type: string;
  locale: string;
  lookback_window: number;
  override_yvc: boolean;
  data_uploader_active: boolean;
  reports_page_active: boolean;
  received_product_demo: boolean;
  repeat_check_fullname_fallback: boolean;
  allow_lead_collection: boolean;
  use_offline_id: boolean;
  fb_ad_authorizer?: User;
  fb_ad_authorizer_id?: number | null;
  fb_page_authorizer?: User;
  fb_page_authorizer_id?: number | null;
  google_authorizer?: User;
  google_authorizer_id?: number | null;
  fb_leads_polling_form_ids?: string[] | null;
  address?: string | null;
  google_type?: string | null;
  google_looker_embed_url?: string | null;
  google_ads_customer_id?: string | null;
  google_ads_manager_id?: string | null;
  fb_builder: boolean;
  adaccount_system_user_id: string | null;
  page_system_user_id: string | null;
  dataset_system_user_id: string | null;
  offline_system_user_id: string | null;
  instagram_system_user_id: string | null;
  sf_account_id?: string | null;
  slack_channel_id?: string | null;
  google_drive_folder_id?: string | null;
  googleDriveFolderID?: string | null;
}

type RequireNonNullable<T, K extends keyof T> = Omit<T, K> &
  {
    [P in K]-?: NonNullable<T[P]>;
  };

type BusinessTokens = {
  fb_ad_access_token?: string | null;
  fb_page_authorizer_access_token?: string | null;
  ad_account_token?: string | null;
  page_token?: string | null;
  dataset_token?: string | null;
  offline_token?: string | null;
  instagram_token?: string | null;
};

type Authorizers = RequireNonNullable<
  BusinessTokens,
  'fb_ad_access_token' | 'fb_page_authorizer_access_token'
> & {
  ad_account_token: never;
  page_token: never;
  dataset_token: never;
  offline_token: never;
  instagram_token: never;
};

type SystemUsers = RequireNonNullable<
  BusinessTokens,
  'ad_account_token' | 'page_token' | 'dataset_token'
> & {
  fb_ad_access_token: never;
  fb_page_authorizer_access_token: never;
};

export function isAuthorizers(tokens: BusinessTokens): tokens is Authorizers {
  return (
    !!tokens.fb_ad_access_token && !!tokens.fb_page_authorizer_access_token
  );
}

export function isSystemTokens(tokens: BusinessTokens): tokens is SystemUsers {
  return (
    !!tokens.ad_account_token && !!tokens.page_token && !!tokens.dataset_token
  );
}

type TokenType = 'ad' | 'page' | 'dataset' | 'offline' | 'instagram';

/**
 * Determines where to pull the token type from given the business tokens.
 * A business only ever has one of either System Users or Authorizers.
 */
export function getToken(
  tokens: BusinessTokens,
  type: Exclude<TokenType, 'dataset' | 'offline' | 'instagram'>,
): string;
export function getToken(
  tokens: BusinessTokens,
  type: Exclude<TokenType, 'ad' | 'page'>,
): string | null | undefined;
export function getToken(
  tokens: BusinessTokens,
  type: TokenType,
): string | null | undefined {
  if (isAuthorizers(tokens)) {
    switch (type) {
      case 'ad':
        return tokens.fb_ad_access_token;
      case 'page':
        return tokens.fb_page_authorizer_access_token;
      case 'dataset':
        return tokens.fb_ad_access_token;
      case 'offline':
        return tokens.fb_ad_access_token;
      case 'instagram':
        return tokens.fb_ad_access_token;
      default:
        throw new Error(
          `Token type '${type}' is not available in Authorizers.`,
        );
    }
  } else if (isSystemTokens(tokens)) {
    switch (type) {
      case 'ad':
        return tokens.ad_account_token;
      case 'page':
        return tokens.page_token;
      case 'dataset':
        return tokens.dataset_token;
      case 'offline':
        return tokens.offline_token;
      case 'instagram':
        return tokens.instagram_token;
      default:
        throw new Error(`Unknown token type: '${type}'.`);
    }
  } else {
    throw new Error('Invalid BusinessTokens object.');
  }
}

export type LegacyBusiness = {
  ingest_access_token: string;
  id: number;
  business_name?: string;
  website?: string;
  fb_account_id: string;
  fb_page_id?: string;
  branch_id?: number;
  first_initial_purchase?: number;
  start?: string;
  focus?: number;
  offline_event_set_id?: string | null;
  fb_dataset_id?: string | null;
  attr_windows: AttrWindowsBySourceType;
  lead_webhook_format?: LeadWebhookFormat;
  lead_webhook_email?: 'off' | 'otf';
  lookback_window: number;
  options?: BusinessManagerBusinessOption[];
  currency_type: string;
  timezone: string;
  yvc_spend?: number;
  yvc_party_size?: number;
  yvc_visits?: number;
  override_yvc?: boolean;
  show_roi?: boolean;
  repeat_check_fullname_fallback?: boolean;
  allow_lead_collection?: boolean;
  city?: string;
  state?: string;
  fb_leads_polling_form_ids?: string[];
  country: CountryCode;
  errors?: BusinessError[];
  tokens: BusinessTokens;
};

export interface BusinessManagerBusinessOption {
  id: number;
  branch_id: number;
  business_id: number;
  branch_options_id: number;
  field_value: string;
  option: {
    id: number;
    branch_id: number;
    name: string;
  };
}

export interface BusinessManagerStage {
  id?: number;
  name: string;
  desc: string;
  icon: string;
  support: Support;
  groups: BusinessManagerGroup[];
  estimate: boolean;
  order: number;
  conversion_name: string;
  est_conversion_name: string;
  show_total: boolean;
  is_kpi: boolean;
  is_sub_kpi: boolean;
  show_overview: boolean;
  cost_per_metric: number;
  data_sources_id?: number;
  campaign_goal?: string;
  stage_target?: StageTarget;
}

export interface BusinessManagerGroup {
  id?: number;
  name: string;
  desc: string;
  sources: string[];
  extrapolation_type: ExtrapolationType;
}

export interface BusinessTag {
  id: number;
  label: string;
  color_hex: string;
  type: number;
  created_at?: Date;
  updated_at?: Date;
}
